import type {
  CreateCustomWeddingSupplierInput,
  CreateWeddingSupplierContactInput,
  SupplierCategory,
  UpdateWeddingSupplierContactInput,
  UpdateWeddingSupplierDetailsInput,
  WeddingSupplierStatus,
} from '@amore/shared'
import { queryOptions } from '@tanstack/react-query'
import type { InferResponseType } from 'hono/client'
import { api, unwrap } from '@/lib/api'

const workspaceApi = api.api.workspaces[':slug']

export const supplierKeys = {
  catalog: (slug: string) => ['catalog-suppliers', slug] as const,
  catalogDetail: (slug: string, catalogSupplierId: string) =>
    ['catalog-suppliers', slug, catalogSupplierId] as const,
  detail: (slug: string, weddingSupplierId: string) =>
    ['wedding-suppliers', slug, weddingSupplierId] as const,
  wedding: (slug: string) => ['wedding-suppliers', slug] as const,
}
export type WeddingSuppliersResponse = InferResponseType<
  (typeof workspaceApi)['wedding-suppliers']['$get'],
  200
>
export type WeddingSupplierItem = WeddingSuppliersResponse['weddingSuppliers'][number]
export type CatalogSuppliersResponse = InferResponseType<
  (typeof workspaceApi)['catalog-suppliers']['$get'],
  200
>
export type CatalogSupplier = CatalogSuppliersResponse['suppliers'][number]
export type CatalogSupplierDetailResponse = InferResponseType<
  (typeof workspaceApi)['catalog-suppliers'][':catalogSupplierId']['$get'],
  200
>
export type WeddingSupplierDetailResponse = InferResponseType<
  (typeof workspaceApi)['wedding-suppliers'][':weddingSupplierId']['$get'],
  200
>
export type WeddingSupplierDetailInput = {
  roles: SupplierCategory[]
  notes?: UpdateWeddingSupplierDetailsInput['notes']
  profile?: UpdateWeddingSupplierDetailsInput['profile']
  contacts: (CreateWeddingSupplierContactInput & { id?: string })[]
}

export const weddingSuppliersQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: supplierKeys.wedding(slug),
    queryFn: () =>
      unwrap<WeddingSuppliersResponse>(workspaceApi['wedding-suppliers'].$get({ param: { slug } })),
  })

export const catalogSuppliersQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: supplierKeys.catalog(slug),
    queryFn: () =>
      unwrap<CatalogSuppliersResponse>(workspaceApi['catalog-suppliers'].$get({ param: { slug } })),
  })

export const catalogSupplierDetailQueryOptions = (slug: string, catalogSupplierId: string) =>
  queryOptions({
    queryKey: supplierKeys.catalogDetail(slug, catalogSupplierId),
    queryFn: () =>
      unwrap<CatalogSupplierDetailResponse>(
        workspaceApi['catalog-suppliers'][':catalogSupplierId'].$get({
          param: { slug, catalogSupplierId },
        }),
      ),
  })

export const weddingSupplierDetailQueryOptions = (slug: string, weddingSupplierId: string) =>
  queryOptions({
    queryKey: supplierKeys.detail(slug, weddingSupplierId),
    queryFn: () =>
      unwrap<WeddingSupplierDetailResponse>(
        workspaceApi['wedding-suppliers'][':weddingSupplierId'].$get({
          param: { slug, weddingSupplierId },
        }),
      ),
  })

export function addCatalogSupplierToWedding(slug: string, catalogSupplierId: string) {
  return unwrap(
    workspaceApi['wedding-suppliers'].$post({ param: { slug }, json: { catalogSupplierId } }),
  )
}

export function reportCatalogCorrectionRequest(
  slug: string,
  catalogSupplierId: string,
  message: string,
) {
  return unwrap(
    workspaceApi['catalog-suppliers'][':catalogSupplierId'].corrections.$post({
      param: { slug, catalogSupplierId },
      json: { message },
    }),
  )
}

export function createCustomWeddingSupplierRequest(
  slug: string,
  input: CreateCustomWeddingSupplierInput,
) {
  return unwrap(workspaceApi['wedding-suppliers'].custom.$post({ param: { slug }, json: input }))
}

export function createWeddingSupplierContactRequest(
  slug: string,
  weddingSupplierId: string,
  input: CreateWeddingSupplierContactInput,
) {
  return unwrap(
    workspaceApi['wedding-suppliers'][':weddingSupplierId'].contacts.$post({
      param: { slug, weddingSupplierId },
      json: input,
    }),
  )
}

export function updateWeddingSupplierContactRequest(
  slug: string,
  weddingSupplierId: string,
  contactId: string,
  input: UpdateWeddingSupplierContactInput,
) {
  return unwrap(
    workspaceApi['wedding-suppliers'][':weddingSupplierId'].contacts[':contactId'].$patch({
      param: { slug, weddingSupplierId, contactId },
      json: input,
    }),
  )
}

export async function deleteWeddingSupplierContactRequest(
  slug: string,
  weddingSupplierId: string,
  contactId: string,
) {
  const response = await workspaceApi['wedding-suppliers'][':weddingSupplierId'].contacts[
    ':contactId'
  ].$delete({ param: { slug, weddingSupplierId, contactId } })
  if (!response.ok) throw new Error('Kontaktní osobu se nepodařilo odstranit.')
}

/** Roles go through the link update, notes and a custom profile through its details. */
export async function saveWeddingSupplierDetailRequest(
  slug: string,
  detail: WeddingSupplierDetailResponse,
  { contacts, notes, profile, roles }: WeddingSupplierDetailInput,
) {
  const weddingSupplierId = detail.weddingSupplier.id
  const submittedIds = new Set(contacts.flatMap((contact) => (contact.id ? [contact.id] : [])))
  const contactWrites = contacts.map(({ id, ...contact }) =>
    id
      ? updateWeddingSupplierContactRequest(slug, weddingSupplierId, id, contact)
      : createWeddingSupplierContactRequest(slug, weddingSupplierId, contact),
  )
  const contactDeletes = detail.contacts
    .filter((contact) => !submittedIds.has(contact.id))
    .map((contact) => deleteWeddingSupplierContactRequest(slug, weddingSupplierId, contact.id))
  const rolesChanged = roles.join() !== detail.weddingSupplier.roles.join()
  await Promise.all([
    unwrap(
      workspaceApi['wedding-suppliers'][':weddingSupplierId'].details.$patch({
        param: { slug, weddingSupplierId },
        json: { notes, profile },
      }),
    ),
    rolesChanged
      ? unwrap(
          workspaceApi['wedding-suppliers'][':weddingSupplierId'].$patch({
            param: { slug, weddingSupplierId },
            json: { roles },
          }),
        )
      : undefined,
    ...contactWrites,
    ...contactDeletes,
  ])
}

export async function updateWeddingSupplierStatus(
  slug: string,
  weddingSupplierId: string,
  status: WeddingSupplierStatus,
) {
  const response = await workspaceApi['wedding-suppliers'][':weddingSupplierId'].$patch({
    param: { slug, weddingSupplierId },
    json: { status },
  })
  if (!response.ok) throw new Error('Stav dodavatele se nepodařilo uložit.')
}

export async function removeWeddingSupplier(slug: string, weddingSupplierId: string) {
  const response = await workspaceApi['wedding-suppliers'][':weddingSupplierId'].$delete({
    param: { slug, weddingSupplierId },
  })
  if (!response.ok) throw new Error('Dodavatele se nepodařilo odstranit.')
}
